import { type ComponentProps, useMemo } from 'react';
import { TextField as TackleTextField } from '@tackle-io/platform-ui';
import { InputLabel } from './InputLabel';

interface TextFieldProps extends ComponentProps<typeof TackleTextField> {
  label: string;
  tooltipText?: string;
  helperText?: string;
  dataId?: string;
}

/** A Tackle style text input field that has:
 *  - a label with or without a tooltip
 *  - helper text
 *  - error message display if provided
 */
const TextInputField = ({
  dataId,
  errorMessage,
  helperText,
  value,
  onChange,
  onBlur,
  name,
  tooltipText,
  required,
  label,
  type,
  ...inputProps
}: TextFieldProps) => {
  const inputTypeProps = useMemo(() => {
    if (type === 'number') {
      return {
        type: 'number',
        mode: 'numberformat',
        decimalScale: 0,
        numberPrefix: '',
      };
    }
    return { type };
  }, [type]);

  return (
    <TackleTextField
      data-id={dataId}
      id={name}
      name={name}
      label={
        <InputLabel
          dataId={dataId}
          tooltipText={tooltipText}
          label={label}
          required={required}
        />
      }
      onChange={(e: React.ChangeEvent<HTMLInputElement>): void => {
        const value = e.target?.value ?? undefined;
        onChange(value);
      }}
      onBlur={onBlur}
      helperText={helperText}
      value={value}
      error={errorMessage}
      inputProps={inputProps}
      disabled={inputProps.disabled}
      noValidate
      multiline={inputProps.multiline}
      rows={inputProps.rows}
      {...inputTypeProps}
    />
  );
};

export default TextInputField;
